/**
 * Properties-to-compare checklist (legacy file name kept for stable test ids).
 *
 * Storage shape is still `ignoredProperties[]` (= props absent from the diff),
 * but the UI presents the inverse: checked = compared / unchecked = skipped.
 */

import { useCallback, useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { DEFAULT_IGNORED_PROPERTIES } from '../types/schemaCompare';
import { ENGINE_PROPERTY_ALLOWLIST } from '../utils/compareDepth';

type PropertyGroup = {
  heading: string;
  properties: { key: string; label: string }[];
};

const PROPERTY_GROUPS: PropertyGroup[] = [
  {
    heading: 'Volatile / storage',
    properties: [
      { key: 'fillFactor', label: 'fillFactor' },
      { key: 'autoIncrement', label: 'autoIncrement' },
      { key: 'pctFree', label: 'pctFree' },
      { key: 'iniTrans', label: 'iniTrans' },
      { key: 'identitySeed', label: 'identitySeed' },
      { key: 'identityIncrement', label: 'identityIncrement' },
    ],
  },
  {
    heading: 'Engine-specific options',
    properties: [
      { key: 'dataCompression', label: 'dataCompression' },
      { key: 'durability', label: 'durability' },
      { key: 'memoryOptimized', label: 'memoryOptimized' },
      { key: 'storageEngine', label: 'storageEngine' },
      { key: 'rowFormat', label: 'rowFormat' },
      { key: 'defaultCharset', label: 'defaultCharset' },
      { key: 'defaultCollation', label: 'defaultCollation' },
      { key: 'tablespace', label: 'tablespace' },
      { key: 'inheritsFrom', label: 'inheritsFrom' },
      { key: 'unlogged', label: 'unlogged' },
      { key: 'pctUsed', label: 'pctUsed' },
      { key: 'compression', label: 'compression' },
      { key: 'logging', label: 'logging' },
      { key: 'organization', label: 'organization' },
      { key: 'withoutRowid', label: 'withoutRowid' },
      { key: 'strict', label: 'strict' },
    ],
  },
  {
    heading: 'Column extras',
    properties: [
      { key: 'computedExpression', label: 'computedExpression' },
      { key: 'collation', label: 'collation' },
    ],
  },
  {
    heading: 'Documentation',
    properties: [{ key: 'comment', label: 'comment' }],
  },
];

type Props = {
  ignoredProperties: string[] | undefined;
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /**
   * When provided, hides properties the engine never emits. When omitted,
   * shows all properties (used by tests and as a safe fallback).
   */
  engine?: DatabaseEngineId;
};

export function IgnoredPropertiesSection({
  ignoredProperties,
  onChange,
  disabled = false,
  engine,
}: Props) {
  const { track } = useAnalytics();

  const ignoredSet = useMemo(
    () => new Set(ignoredProperties ?? DEFAULT_IGNORED_PROPERTIES),
    [ignoredProperties]
  );

  const visibleGroups = useMemo(() => {
    if (!engine) return PROPERTY_GROUPS;
    const allow = new Set(ENGINE_PROPERTY_ALLOWLIST[engine] ?? []);
    return PROPERTY_GROUPS
      .map((group) => ({
        ...group,
        properties: group.properties.filter((p) => allow.has(p.key)),
      }))
      .filter((group) => group.properties.length > 0);
  }, [engine]);

  const handleToggle = useCallback(
    (property: string, compared: boolean) => {
      // UI says "checked = compared". Internally we still persist the
      // *ignored* set: compared items are absent from the array.
      const next = new Set(ignoredProperties ?? DEFAULT_IGNORED_PROPERTIES);
      if (compared) next.delete(property);
      else next.add(property);
      onChange(Array.from(next));
      track('feature:schema_compare_toggle_ignore_property', {
        property,
        enabled: !compared,
      });
    },
    [ignoredProperties, onChange, track]
  );

  return (
    <div data-testid="ignored-properties-section">
      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1">
        Properties to compare
      </h4>
      <p className="text-xs text-muted-foreground mb-3">
        Checked properties are <strong className="font-medium">included</strong> in the
        diff. Unchecked properties are skipped.
      </p>
      <div className="space-y-4">
        {visibleGroups.map((group) => (
          <div key={group.heading}>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {group.heading}
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {group.properties.map((p) => {
                const id = `option-ignore-${p.key}`;
                const compared = !ignoredSet.has(p.key);
                return (
                  <div key={p.key} className="flex items-center gap-2">
                    <Checkbox
                      id={id}
                      checked={compared}
                      onCheckedChange={(value) =>
                        handleToggle(p.key, value === true)
                      }
                      disabled={disabled}
                      data-testid={id}
                    />
                    <Label
                      htmlFor={id}
                      className={`text-sm cursor-pointer font-mono ${
                        compared ? '' : 'line-through text-muted-foreground'
                      }`}
                    >
                      {p.label}
                    </Label>
                    {!compared && (
                      <span
                        className="text-[10px] uppercase tracking-wider px-1 py-0 rounded bg-muted text-muted-foreground"
                        aria-hidden="true"
                      >
                        Skipped
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
